import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Eye, EyeSlash } from '@gravity-ui/icons';
import { registerUser } from '../../services/auth';
import { CURRENT_OFERTA_VERSION, OFERTA_PDF_PATH, CURRENT_POLITICS_VERSION, POLITICS_PDF_PATH } from '../../constants/offers';
import '../LoginPage/LoginPage.css';
import './RegisterPage.css';

const INITIAL_FORM = {
  email: '',
  password: '',
  confirmPassword: '',
};

const RegisterPage = () => {
  const [form, setForm] = useState(INITIAL_FORM);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [ofertaAccepted, setOfertaAccepted] = useState(false);
  const [politicsAccepted, setPoliticsAccepted] = useState(false);

  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.email.trim()) {
      setError('Введите email');
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      setError('Введите корректный email');
      return;
    }

    if (form.password.length < 6) {
      setError('Пароль — минимум 6 символов');
      return;
    }

    if (form.password !== form.confirmPassword) {
      setError('Пароли не совпадают');
      return;
    }

    if (!ofertaAccepted) {
      setError('Необходимо принять условия оферты');
      return;
    }

    if (!politicsAccepted) {
      setError('Необходимо принять политику обработки ПД');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      await registerUser({
        email: form.email.trim(),
        password: form.password,
        oferta_version: CURRENT_OFERTA_VERSION,
        politics_version: CURRENT_POLITICS_VERSION,
      });

      setSuccessMessage('Регистрация успешна. Перенаправление на вход…');
      window.dispatchEvent(new Event('auth-change'));
      setTimeout(() => navigate('/login', { replace: true }), 2000);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (Array.isArray(detail)) {
        setError(detail.map((item) => item.msg).join(' '));
      } else {
        setError(err.response?.data?.message || err.response?.data?.detail || 'Ошибка регистрации');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="register-container auth-page">
      <div className="register-card">
        <form onSubmit={handleSubmit} className="register-form">
          <h2 className="register-title">Регистрация</h2>

          <input name="email" type="email" placeholder="Email" value={form.email} onChange={handleChange} className="register-input" />

          <div className="register-password-container">
            <input
              name="password"
              type={showPassword ? 'text' : 'password'}
              placeholder="Пароль"
              value={form.password}
              onChange={handleChange}
              className="register-input"
            />
            <button
              type="button"
              className="password-toggle-btn"
              onClick={() => setShowPassword(!showPassword)}
              aria-label={showPassword ? 'Скрыть пароль' : 'Показать пароль'}
              title={showPassword ? 'Скрыть пароль' : 'Показать пароль'}
            >
              {showPassword
                ? <EyeSlash width={16} height={16} aria-hidden="true" className="eye-icon" />
                : <Eye width={16} height={16} aria-hidden="true" className="eye-icon" />}
            </button>
          </div>

          <div className="register-password-container">
            <input
              name="confirmPassword"
              type={showPassword ? 'text' : 'password'}
              placeholder="Повторите пароль"
              value={form.confirmPassword}
              onChange={handleChange}
              className="register-input"
            />
          </div>

          <label className="register-oferta">
            <input
              type="checkbox"
              checked={ofertaAccepted}
              onChange={(e) => setOfertaAccepted(e.target.checked)}
            />
            <span>
              Принимаю условия{' '}
              <a href={OFERTA_PDF_PATH} target="_blank" rel="noopener noreferrer">
                оферты
              </a>
            </span>
          </label>

          <label className="register-oferta">
            <input
              type="checkbox"
              checked={politicsAccepted}
              onChange={(e) => setPoliticsAccepted(e.target.checked)}
            />
            <span>
              Принимаю{' '}
              <a href={POLITICS_PDF_PATH} target="_blank" rel="noopener noreferrer">
                политику обработки персональных данных
              </a>
            </span>
          </label>

          {error && <div className="auth-message auth-message--error">{error}</div>}
          {successMessage && <div className="auth-message auth-message--success">{successMessage}</div>}

          <button type="submit" className="register-btn" disabled={isSubmitting}>
            {isSubmitting ? 'Регистрация…' : 'Зарегистрироваться'}
          </button>

          <div className="register-login-link">
            Уже есть аккаунт? <Link to="/login">Войти</Link>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RegisterPage;
